import React, { useState } from 'react';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
	useGetProjectTagsQuery,
	useUpdateTaskTagMutation
} from '@features/project-management-tools/api/projectManagementApi';
import { TAG_COLORS_LIST } from '@features/project-management-tools/constents';

import { TAG_COLOR_LIST } from '@/constants/constant';

import CommonImage from '@/components/CommonImage';
import InputField from '@/components/InputField';
import { toastError } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';

const INITIAL_INPUT_VALUES = {
	colorCode: TAG_COLOR_LIST[0],
	id: null,
	projectId: null,
	projectScope: '',
	status: '',
	text: '',
	type: '',
	sl: null
};

// SortableTag component for individual tags
const SortableTag = ({ tag, onSelect, onEdit }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: tag.id.toString()
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		background: tag?.colorCode || '#d1d1d1',
		opacity: isDragging ? 0.5 : 1,
		zIndex: isDragging ? 999 : 1,
		cursor: isDragging ? 'grabbing' : 'pointer'
	};

	return (
		<li
			ref={setNodeRef}
			className="d-flex justify-content-between gray_dark_02 px-2 py-2 mb-2 align-items-center"
			style={style}
			onClick={() => onSelect(tag)}
			{...attributes}
			{...listeners}
		>
			<p className="text-white weight-600">{tag?.text}</p>
			<CommonImage
				className="ml-2 edit-icon"
				src="/icons/edit-black-20.svg"
				alt="Edit-icon"
				width={18}
				height={18}
				onClick={(e) => {
					e.stopPropagation();
					onEdit(tag);
				}}
			/>
		</li>
	);
};

const TaskDndTagsModal = ({
	tagsType,
	selectedTableColumnToEdit,
	requestBodyKey,
	id,
	updateTask,
	refetchTasks,
	resetEditStates,
	refetchProjectDetails,
	refetchProjectTags: refetchProjectTagsFromParent
}) => {
	const [isShowUpdateTags, setIsShowUpdateTags] = useState(false);
	const [selectedTagToEdit, setSelectedTagToEdit] = useState(null);
	const [inputValue, setInputValue] = useState(INITIAL_INPUT_VALUES);
	const [tags, setTags] = useState([]);

	const {
		data: projectTags,
		isLoading: tagsLoading,
		refetch: refetchProjectTags
	} = useGetProjectTagsQuery({
		params: { tagType: tagsType, sort: 'sl,asc' },
		projectId: id
	});

	const [updateTaskTag] = useUpdateTaskTagMutation();

	// Update local tags state when projectTags changes
	React.useEffect(() => {
		if (projectTags && projectTags.length > 0) {
			setTags(projectTags);
		}
	}, [projectTags]);

	// Configure sensors for drag and drop
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				delay: 200,
				tolerance: 5
			}
		})
	);

	const handleSelectStatus = (tag) => {
		updateTask({
			body: {
				id: selectedTableColumnToEdit?.id,
				[requestBodyKey]: tag
			},
			projectId: id
		})
			.unwrap()
			.then(() => {
				refetchTasks();
				refetchProjectTags();
				resetEditStates();
				refetchProjectDetails && refetchProjectDetails();
				refetchProjectTagsFromParent();
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Failed to update status');
			});
	};

	const handleCreateTag = () => {
		const requestBody = {
			text: inputValue?.text,
			colorCode: inputValue?.colorCode,
			projectId: parseInt(id)
		};
		//Hits Update Task api to create new Tags
		updateTask({
			body: {
				[requestBodyKey]: requestBody,
				id: selectedTableColumnToEdit?.id
			},
			projectId: id
		})
			.unwrap()
			.then(() => {
				refetchTasks();
				refetchProjectTags();
				setIsShowUpdateTags(false);
				setInputValue(INITIAL_INPUT_VALUES);
				setSelectedTagToEdit(null);
			});
	};

	const handleUpdateTaskTag = () => {
		const requestBody = {
			id: selectedTagToEdit?.id,
			text: inputValue?.text,
			colorCode: inputValue?.colorCode,
			projectId: parseInt(id),
			sl: selectedTagToEdit?.sl
		};
		// Hits Update Tag api to update existing Tags
		updateTaskTag({
			body: requestBody
		})
			.unwrap()
			.then(() => {
				refetchTasks();
				refetchProjectTags();
				setIsShowUpdateTags(false);
				setInputValue(INITIAL_INPUT_VALUES);
				setSelectedTagToEdit(null);
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Failed to create tag');
			});
	};

	// Handle drag end event
	const handleDragEnd = (event) => {
		const { active, over } = event;

		if (!active || !over || active.id === over.id) return;

		const oldIndex = tags.findIndex((tag) => tag.id.toString() === active.id);
		const newIndex = tags.findIndex((tag) => tag.id.toString() === over.id);

		if (oldIndex !== -1 && newIndex !== -1) {
			// Reorder the tags array
			const newTags = arrayMove(tags, oldIndex, newIndex);
			setTags(newTags);

			// Calculate new SL based on position
			let newSl;
			if (newIndex === 0) {
				// If dropped at the top, get highest SL and add 1
				const lowestSl = Math.min(...newTags.map((tag) => tag.sl));

				newSl = lowestSl - 1;
			} else if (newIndex === newTags.length - 1) {
				// If dropped at the bottom, get lowest SL and subtract 1
				const highestSl = Math.max(...newTags.map((tag) => tag.sl));

				newSl = highestSl + 1;
			} else {
				// If dropped in the middle, create average of prev and next item's SL
				const prevSl = newTags[newIndex - 1].sl;
				const nextSl = newTags[newIndex].sl;
				newSl = (prevSl + nextSl) / 2;
			}

			// Update the tag's SL
			updateTaskTag({
				body: {
					id: newTags[newIndex]?.id,
					sl: newSl,
					projectId: parseInt(id)
				}
			})
				.unwrap()
				.then(() => {
					refetchProjectTagsFromParent();
					refetchTasks();
				});
		}
	};

	return (
		<LoaderComponent loading={tagsLoading}>
			<div className="task-dnd-tags-modal shadow-8dp bg-white">
				<div className="modal-content">
					<div className="common-popup">
						<div className="common-popup-body scroll-y-label p-3">
							{isShowUpdateTags && (
								<div className="row">
									<div className="d-flex justify-content-between align-items-end">
										<div className="col-md-8 p-0">
											<InputField
												id="tagName"
												type="text"
												name="tagName"
												autoFocus
												value={inputValue?.text}
												onChange={(e) =>
													setInputValue((prev) => ({
														...prev,
														text: e.target.value
													}))
												}
												wrapperClassName="form-group mb-0"
											/>
										</div>
										<div className="col-md-4 p-0">
											<button
												className="button outline size32 ml-2"
												onClick={(e) => {
													e.stopPropagation();
													isShowUpdateTags && selectedTagToEdit?.id
														? handleUpdateTaskTag()
														: handleCreateTag();
												}}
											>
												{!inputValue?.id && (
													<CommonImage
														src="/icons/plus-20.svg"
														alt="Add-icon"
														width={20}
														height={20}
													/>
												)}
												{inputValue?.id ? 'Update' : 'Add'}
											</button>
										</div>
									</div>

									<div className="col-md-12 px-0 gap-2 pb-2">
										<div className="select-tag-dropdown shadow-none w-100 position-static">
											<div className="create">
												<div className="colors px-0">
													{TAG_COLORS_LIST?.map((item, index) => (
														<span
															key={`tag_color_$${index}`}
															className={`single-color ${inputValue?.colorCode === item ? 'active' : ''}`}
															style={{ background: item }}
															onClick={() =>
																setInputValue((prev) => ({
																	...prev,
																	colorCode: item
																}))
															}
														/>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
							<div className="edit-status-list">
								{!tagsLoading && !isShowUpdateTags && tags && tags.length > 0 && (
									<DndContext
										sensors={sensors}
										collisionDetection={closestCenter}
										onDragEnd={handleDragEnd}
									>
										<SortableContext
											items={tags.map((tag) => tag.id.toString())}
											strategy={verticalListSortingStrategy}
										>
											<ul>
												{tags.map((tag) => (
													<SortableTag
														key={tag.id}
														tag={tag}
														onSelect={handleSelectStatus}
														onEdit={(tag) => {
															setInputValue(tag);
															setIsShowUpdateTags(true);
															setSelectedTagToEdit(tag);
														}}
													/>
												))}
											</ul>
										</SortableContext>
									</DndContext>
								)}
								{!isShowUpdateTags && (
									<div className="p-0 mt-3 text-right">
										<button
											type="submit"
											className="button outline size32"
											onClick={() => setIsShowUpdateTags(true)}
										>
											<CommonImage
												src="/icons/plus-20.svg"
												alt="Add-icon"
												width={20}
												height={20}
											/>
											Add
										</button>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default TaskDndTagsModal;
